import { cn } from '@doccareplus/shared/lib/utils';

export interface TabItem {
  id: string;
  label: string;
  /** Optional count badge shown next to the label (e.g. active alert count). */
  badge?: number;
}

export interface TabsProps {
  items: TabItem[];
  /** Selected tab id. */
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

/**
 * Tabs — controlled tab strip. The parent owns which content renders for the
 * selected id; this component only renders the strip and reports clicks.
 *
 * Renders as horizontally-scrolling rounded chips below the `md` breakpoint
 * (matching the mobile surface split at 767px used elsewhere — see
 * `useIsMobileSurface`) and as an underlined tab row at `md` and up. Pure
 * CSS responsive classes — no viewport JS, so there's no layout flash.
 *
 * NO business logic — pure presentational; the parent owns the selected tab state.
 */
export function Tabs({ items, value, onChange, className }: TabsProps) {
  return (
    <div
      role="tablist"
      className={cn('scroll-hide flex gap-2 overflow-x-auto md:gap-1 md:border-b md:border-[var(--border)]', className)}
    >
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(item.id)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-[12.5px] font-semibold transition-colors',
              'md:rounded-none md:border-x-0 md:border-t-0 md:border-b-2 md:px-3.5 md:py-2.5 md:text-[13.5px]',
              selected
                ? 'border-transparent bg-[var(--primary)] text-[var(--primary-fg)] md:border-b-[var(--primary)] md:bg-transparent md:text-[var(--primary)]'
                : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] hover:text-[var(--text)] md:border-b-transparent md:bg-transparent'
            )}
          >
            {item.label}
            {item.badge != null && item.badge > 0 && (
              <span className="rounded-full bg-[var(--surface-3)] px-1.5 py-0.5 text-[11px] font-bold text-[var(--text-2)]">
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
